import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Edit2, Trash2, Eye, EyeOff, Sparkles, Save, GraduationCap, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const blank = { title: "", description: "", youtube_url: "", category: "", tags: [], order: 0, featured: false, active: true };

export default function MasterTutoriais() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [savingSettings, setSavingSettings] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadAll = async () => {
    const [a, b] = await Promise.all([
      api.get("/master/tutorials"),
      api.get("/public/tutorial-settings"),
    ]);
    setItems(a.data); setSettings(b.data);
  };
  useEffect(() => { loadAll(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...blank, order: items.length }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...blank, ...t, tags: t.tags || [] }); setOpen(true); };
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.title.trim() || !form.youtube_url.trim()) { toast.error("Título e link do YouTube são obrigatórios"); return; }
    try {
      if (editing) {
        const r = await api.put(`/master/tutorials/${editing.id}`, form);
        setItems(prev => prev.map(t => t.id === editing.id ? r.data : t));
      } else {
        const r = await api.post("/master/tutorials", form);
        setItems(prev => [...prev, r.data].sort((a, b) => (a.order||0) - (b.order||0)));
      }
      setOpen(false); toast.success("Salvo");
    } catch (e) {
      // O backend pode retornar detail como string (ex: "Link do YouTube inválido…")
      // ou como objeto (payloads de bloqueio de assinatura). Normaliza pra texto.
      const d = e?.response?.data?.detail;
      let msg = "Erro ao salvar";
      if (typeof d === "string" && d.trim()) msg = d;
      else if (d && typeof d === "object" && typeof d.message === "string") msg = d.message;
      else if (e?.response?.status === 404) msg = "Tutorial não encontrado — atualize a página e tente novamente.";
      else if (e?.message) msg = e.message;
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este tutorial?")) return;
    await api.delete(`/master/tutorials/${id}`);
    setItems(prev => prev.filter(t => t.id !== id));
    toast.success("Excluído");
  };

  const toggleActive = async (t) => {
    const r = await api.put(`/master/tutorials/${t.id}`, { active: !t.active });
    setItems(prev => prev.map(x => x.id === t.id ? r.data : x));
  };
  const toggleFeatured = async (t) => {
    const r = await api.put(`/master/tutorials/${t.id}`, { featured: !t.featured });
    setItems(prev => prev.map(x => x.id === t.id ? r.data : x));
  };
  const updateOrder = async (t, delta) => {
    const next = (t.order || 0) + delta;
    const r = await api.put(`/master/tutorials/${t.id}`, { order: next });
    setItems(prev => prev.map(x => x.id === t.id ? r.data : x).sort((a, b) => (a.order||0) - (b.order||0)));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/master/tutorial-settings", settings);
      setSettings(r.data); toast.success("Settings salvos");
    } catch { toast.error("Erro"); }
    finally { setSavingSettings(false); }
  };

  const usettings = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const tutoriaisUrl = useMemo(() => typeof window !== "undefined" ? `${window.location.origin}/tutoriais` : "", []);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(tutoriaisUrl); setCopied(true); toast.success("Link copiado"); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  return (
    <div className="space-y-8" data-testid="master-tutorials-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny mb-2">Master · Central de ajuda</p>
          <h1 className="font-display text-4xl tracking-tight flex items-center gap-3"><GraduationCap size={32}/> Tutoriais</h1>
          <p className="text-sm text-neutral-500 mt-1">Cadastre vídeos do YouTube e organize a central de ajuda dos seus assinantes.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={copyLink} className="h-9 text-xs">{copied ? <Check size={14} className="mr-1.5"/> : <Copy size={14} className="mr-1.5"/>} Copiar link público</Button>
          <Button variant="outline" onClick={()=>window.open("/tutoriais","_blank")} className="h-9 text-xs"><ExternalLink size={14} className="mr-1.5"/> Ver como assinante</Button>
          <Button onClick={openNew} className="bg-[#0A0A0A] text-white hover:bg-black h-9 text-xs" data-testid="master-tutorial-new"><Plus size={14} className="mr-1.5"/> Novo tutorial</Button>
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-tutorials-list">Tutoriais ({items.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-tutorials-settings">Configurações</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6">
          {items.length === 0 ? (
            <Card className="p-12 text-center border-[#E5E5E5] shadow-none">
              <GraduationCap size={40} className="mx-auto mb-3 text-neutral-300"/>
              <p className="text-sm text-neutral-500">Nenhum tutorial cadastrado ainda.</p>
              <Button onClick={openNew} className="bg-[#0A0A0A] text-white mt-4"><Plus size={14} className="mr-1.5"/> Cadastrar primeiro</Button>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(t => (
                <Card key={t.id} className={`overflow-hidden border-[#E5E5E5] shadow-none ${!t.active ? "opacity-50" : ""}`} data-testid={`master-tutorial-${t.id}`}>
                  <div className="aspect-video bg-black relative">
                    {t.thumbnail && <img src={t.thumbnail} alt="" className="w-full h-full object-cover"/>}
                    {t.featured && <div className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1"><Sparkles size={10}/> Destaque</div>}
                    {!t.active && <div className="absolute inset-0 bg-black/50 grid place-items-center text-white text-xs">Inativo</div>}
                  </div>
                  <div className="p-3">
                    {t.category && <p className="text-[10px] uppercase tracking-widest text-emerald-600 mb-1">{t.category}</p>}
                    <h3 className="text-sm font-medium truncate">{t.title}</h3>
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-neutral-400">
                      <span className="font-mono">#{(t.order ?? 0)}</span>
                      {(t.views || 0) > 0 && <span className="inline-flex items-center gap-0.5"><Eye size={10}/> {t.views}</span>}
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-1">
                      <div className="flex gap-0.5">
                        <Button size="sm" variant="ghost" onClick={()=>updateOrder(t, -1)} className="h-7 px-1.5 text-xs">↑</Button>
                        <Button size="sm" variant="ghost" onClick={()=>updateOrder(t, 1)} className="h-7 px-1.5 text-xs">↓</Button>
                      </div>
                      <div className="flex gap-0.5">
                        <button onClick={()=>toggleFeatured(t)} className={`p-1.5 rounded text-xs ${t.featured ? "text-amber-500 bg-amber-50" : "text-neutral-400 hover:bg-neutral-100"}`} title="Destaque"><Sparkles size={12}/></button>
                        <button onClick={()=>toggleActive(t)} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500" title={t.active ? "Desativar" : "Ativar"}>
                          {t.active ? <Eye size={12}/> : <EyeOff size={12}/>}
                        </button>
                        <button onClick={()=>openEdit(t)} className="p-1.5 rounded hover:bg-neutral-100" title="Editar"><Edit2 size={12}/></button>
                        <button onClick={()=>remove(t.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Excluir"><Trash2 size={12}/></button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card className="p-6 border-[#E5E5E5] shadow-none space-y-4 max-w-2xl">
            <div className="flex items-center justify-between">
              <div>
                <Label>Central de ajuda ativa?</Label>
                <p className="text-[11px] text-neutral-500 mt-0.5">Se desligar, o item some da sidebar dos assinantes.</p>
              </div>
              <Switch checked={settings.enabled !== false} onCheckedChange={(v)=>usettings("enabled", v)}/>
            </div>
            <div>
              <Label>Nome do menu (sidebar)</Label>
              <Input value={settings.menu_label || ""} onChange={e=>usettings("menu_label", e.target.value)} placeholder="Como Usar" data-testid="settings-menu-label"/>
              <p className="text-[10px] text-neutral-400 mt-1">Sugestões: Como Usar · Central de Ajuda · Tutoriais</p>
            </div>
            <div>
              <Label>WhatsApp de suporte</Label>
              <Input value={settings.whatsapp_support || ""} onChange={e=>usettings("whatsapp_support", e.target.value.replace(/\D/g, ""))} placeholder="51998109841" data-testid="settings-whatsapp"/>
              <p className="text-[10px] text-neutral-400 mt-1">DDD + número (sem espaços). Brasil: +55 já é prefixado automaticamente.</p>
            </div>
            <div>
              <Label>Mensagem padrão</Label>
              <Input value={settings.whatsapp_message || ""} onChange={e=>usettings("whatsapp_message", e.target.value)}/>
            </div>
            <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#0A0A0A] text-white" data-testid="settings-save">
              <Save size={14} className="mr-1.5"/> {savingSettings ? "Salvando…" : "Salvar configurações"}
            </Button>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={(o)=>!o && setOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar tutorial" : "Novo tutorial"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Título *</Label><Input value={form.title} onChange={e=>update("title", e.target.value)} data-testid="form-tutorial-title"/></div>
            <div className="sm:col-span-2"><Label>Link do YouTube *</Label><Input value={form.youtube_url} onChange={e=>update("youtube_url", e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid="form-tutorial-url"/></div>
            <div className="sm:col-span-2"><Label>Descrição curta</Label><Textarea rows={3} value={form.description} onChange={e=>update("description", e.target.value)}/></div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={e=>update("category", e.target.value)} placeholder="Vendas, Estoque, CRM…"/></div>
            <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={e=>update("order", Number(e.target.value)||0)}/></div>
            <div className="sm:col-span-2"><Label>Tags (separadas por vírgula)</Label>
              <Input value={(form.tags || []).join(", ")} onChange={e=>update("tags", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} placeholder="como vender, ver lucro, kanban"/>
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-[#E5E5E5]">
              <span className="text-sm">Em destaque</span>
              <Switch checked={!!form.featured} onCheckedChange={(v)=>update("featured", v)}/>
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-[#E5E5E5]">
              <span className="text-sm">Ativo</span>
              <Switch checked={form.active !== false} onCheckedChange={(v)=>update("active", v)}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#0A0A0A] text-white" data-testid="form-tutorial-submit">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
